/**
 * OpportunityForm Component
 * Multi-step form for creating and editing opportunities.
 * Follows the OrganizationOnboarding.jsx pattern.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
    ProgressBar,
    Spinner,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import useFullAuth from '@/features/auth/hooks/useFullAuth';
import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useOpportunityApi } from '@/shared/services/opportunityApi';

import { isEventType, isFundingType } from '../utils/opportunityConstants';

import {
    StepBasics,
    StepAudienceLocation,
    StepScheduleCommitment,
    StepApplicationContact,
    StepEventDetails,
    StepFundingDetails,
    StepReview,
} from './OpportunityFormSteps';

import '../Opportunities.css';

/**
 * Build the initial form-data shape for the multi-step opportunity wizard.
 * `state` and `eventTimezone` start blank -- they're derived along
 * ownership from the owning org (ticket #307), not from the ecosystem.
 * The org fetch is async (see the org-derived pre-fill effect below), so
 * this initializer can't seed them synchronously the way the old
 * ecosystem-taxonomy values could; the pre-fill happens as a follow-up
 * effect once the org has loaded.
 *
 * @returns {object} The form's initial state.
 */
export function buildInitialFormData() {
    return {
        title: '',
        type: '',
        description: '',
        focusAreas: '',
        tags: '',
        // Audience & Location
        targetAudience: [],
        regions: [],
        locationMode: 'in-person',
        venueName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        // No form field ever sets this (no geocoding UI yet); it exists
        // purely so an edit-mode save round-trips whatever coordinates the
        // opportunity already has instead of the submit wiping them -- see
        // buildSubmitData and the edit-mode load effect below.
        coordinates: null,
        // Schedule & Commitment
        startDate: '',
        endDate: '',
        isOngoing: false,
        commitmentType: '',
        hoursPerWeek: '',
        duration: '',
        isPaid: false,
        compensationDetails: '',
        capacity: '',
        // Application & Contact
        applicationMethod: 'external_link',
        applicationUrl: '',
        applicationEmail: '',
        applicationDeadline: '',
        isRolling: false,
        requirements: '',
        skillsDeveloped: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        // Event Details
        eventStartTime: '',
        eventEndTime: '',
        eventTimezone: '',
        eventIsRecurring: false,
        recurrencePattern: '',
        recurrenceEndDate: '',
        registrationRequired: false,
        registrationUrl: '',
        virtualLink: '',
        // Funding Details
        fundingType: '',
        fundingAmountMin: '',
        fundingAmountMax: '',
        fundingIsEstimate: false,
        fundingFunder: '',
        fundingAwardCycle: '',
        fundingNumberOfAwards: '',
        fundingDuration: '',
        fundingEligibleOrgTypes: [],
        fundingEligibleActivities: '',
        fundingMatchRequired: false,
        fundingMatchDetails: '',
        fundingSeekingPartners: false,
        fundingPartnerRequirements: '',
        fundingPartnerContactName: '',
        fundingPartnerContactEmail: '',
    };
}

OpportunityForm.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']),
};

export default function OpportunityForm({ mode = 'create' }) {
    const { orgId, id } = useParams();
    const navigate = useNavigate();
    const { user: firebaseUser, loading: authLoading } = useAuth();
    const { user: currentUser } = useFullAuth();
    // Ecosystem-admin override: `User.role === 'admin'` no longer exists
    // post-P4a; admin authority for per-tenant resources comes from
    // EcosystemMembership.role in the active tenant (review finding 9).
    const { role: membershipRole, isMember } = useMembership();
    const isEcosystemAdmin = isMember && membershipRole === 'admin';

    const orgApi = useOrganizationApi();
    const opportunityApi = useOpportunityApi();

    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(() => buildInitialFormData());
    const [validationErrors, setValidationErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Enum data from API
    const [targetAudiences, setTargetAudiences] = useState([]);
    const [opportunityTypes, setOpportunityTypes] = useState([]);
    const [regions, setRegions] = useState([]);
    const [organizationSectors, setOrganizationSectors] = useState([]);

    // Organization permission check
    const [orgName, setOrgName] = useState('');
    const [authorized, setAuthorized] = useState(null);
    // Owning org's location/timezone (ticket #307): the source for the
    // create-mode pre-fill effect below. Captured from the same org fetch
    // the permission check already does -- no extra request.
    const [orgLocation, setOrgLocation] = useState(null);

    // Compute dynamic steps based on selected type
    const steps = useMemo(() => {
        const baseSteps = [
            { num: 1, title: 'Basics', icon: '1' },
            { num: 2, title: 'Audience & Location', icon: '2' },
            { num: 3, title: 'Schedule & Commitment', icon: '3' },
            { num: 4, title: 'Application & Contact', icon: '4' },
        ];

        let nextNum = 5;
        if (isEventType(formData.type)) {
            baseSteps.push({
                num: nextNum++,
                title: 'Event Details',
                icon: String(nextNum - 1),
            });
        }
        if (isFundingType(formData.type)) {
            baseSteps.push({
                num: nextNum++,
                title: 'Funding Details',
                icon: String(nextNum - 1),
            });
        }
        baseSteps.push({
            num: nextNum,
            title: 'Review',
            icon: String(nextNum),
        });

        return baseSteps;
    }, [formData.type]);

    const totalSteps = steps.length;
    const isReviewStep = step === steps[steps.length - 1].num;

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !firebaseUser) {
            navigate('/login', {
                state: { from: `/organizations/${orgId}/opportunities/new` },
            });
        }
    }, [firebaseUser, authLoading, navigate, orgId]);

    // Load org data and verify permission.
    // v1.0.1 #50: the previous guard `if (currentUser && orgId)` only ran the
    // effect when the full RILE Connect User profile had loaded. For a brand-
    // new Firebase signup whose `/me` returned 404 (no profile row yet),
    // `currentUser` stays null forever: the effect never fires, `authorized`
    // stays `null`, and the page sits on the loading spinner indefinitely.
    // Now: once auth has finished loading, EITHER we have a full user (run
    // the permission check) OR we don't (deny). No more terminal-spinner
    // state.
    useEffect(() => {
        if (authLoading) {
            // Wait for auth to settle before deciding anything.
            return;
        }
        if (!currentUser || !orgId) {
            // Auth is done resolving and there's no profile to authorize.
            // The user is most likely a fresh Firebase signup who hasn't
            // completed `/create-profile`; surface a 403 view with a path
            // to fix it rather than spinning forever.
            setAuthorized(false);
            return;
        }
        const checkPermission = async () => {
            try {
                const org = await orgApi.fetchOrganization(orgId);
                setOrgName(org.name);
                setOrgLocation(org.location || null);
                const canEdit = org.managers?.some(
                    (m) =>
                        m.user === currentUser._id &&
                        ['owner', 'admin'].includes(m.permissionRole)
                );
                setAuthorized(canEdit || isEcosystemAdmin);
            } catch {
                setAuthorized(false);
            }
        };
        checkPermission();
    }, [authLoading, currentUser, orgId, orgApi, isEcosystemAdmin]);

    // Org-derived pre-fill (ticket #307, client-side UX only -- the actual
    // guarantee that absent fields inherit the org's values is enforced
    // server-side in opportunity.controller.js's applyOrgLocationDefaults).
    // Fills only fields still at their blank initial value, so it never
    // clobbers input the user has already typed. Create-mode only: an
    // existing opportunity's edit-mode load below reads its own stored
    // values, not the org's current ones.
    useEffect(() => {
        if (mode !== 'create' || !orgLocation) return;
        setFormData((prev) => {
            // Venue fields only make sense for an in-person/hybrid
            // opportunity: a remote one has no physical location, and
            // these inputs aren't even rendered for that mode (mirrors
            // the server-side gate in applyOrgLocationDefaults).
            const isPhysical = prev.locationMode !== 'remote';
            return {
                ...prev,
                state: isPhysical
                    ? prev.state || orgLocation.state || ''
                    : prev.state,
                address: isPhysical
                    ? prev.address || orgLocation.address || ''
                    : prev.address,
                city: isPhysical
                    ? prev.city || orgLocation.city || ''
                    : prev.city,
                zipCode: isPhysical
                    ? prev.zipCode || orgLocation.zipCode || ''
                    : prev.zipCode,
                eventTimezone: prev.eventTimezone || orgLocation.timezone || '',
            };
        });
    }, [mode, orgLocation]);

    // Load enum data
    useEffect(() => {
        const loadEnums = async () => {
            try {
                const [typesData, regionsData, sectorsData] = await Promise.all(
                    [
                        opportunityApi.fetchTypes(),
                        orgApi.getRegions(),
                        orgApi.getSectors(),
                    ]
                );
                setTargetAudiences(typesData.targetAudiences || []);
                setOpportunityTypes(typesData.opportunityTypes || []);
                setRegions(regionsData || []);
                setOrganizationSectors(sectorsData || []);
            } catch (err) {
                console.error('Failed to load enum data:', err);
            }
        };
        loadEnums();
    }, [opportunityApi, orgApi]);

    // Load existing opportunity for edit mode
    useEffect(() => {
        if (mode !== 'edit' || !id) return;

        const loadOpportunity = async () => {
            try {
                setLoading(true);
                const opp = await opportunityApi.fetchOpportunity(id);

                // Map server data to flat form state
                setFormData({
                    title: opp.title || '',
                    type: opp.type || '',
                    description: opp.description || '',
                    focusAreas: (opp.focusAreas || []).join(', '),
                    tags: (opp.tags || []).join(', '),
                    targetAudience: opp.targetAudience || [],
                    regions: opp.regions || [],
                    locationMode: opp.location?.isHybrid
                        ? 'hybrid'
                        : opp.location?.isRemote
                          ? 'remote'
                          : 'in-person',
                    venueName: opp.location?.venueName || '',
                    address: opp.location?.address || '',
                    city: opp.location?.city || '',
                    state: opp.location?.state || '',
                    zipCode: opp.location?.zipCode || '',
                    // Guard against a malformed/empty point (e.g. Mongoose's
                    // array-default shape on a freshly-constructed document
                    // that was never round-tripped through the DB) rather
                    // than round-tripping it verbatim -- Joi's .length(2)
                    // would reject a non-2-length array on save.
                    coordinates:
                        opp.location?.coordinates?.coordinates?.length === 2
                            ? opp.location.coordinates
                            : null,
                    startDate: opp.dates?.startDate
                        ? opp.dates.startDate.substring(0, 10)
                        : '',
                    endDate: opp.dates?.endDate
                        ? opp.dates.endDate.substring(0, 10)
                        : '',
                    isOngoing: opp.dates?.isOngoing || false,
                    commitmentType: opp.commitment?.type || '',
                    hoursPerWeek: opp.commitment?.hoursPerWeek || '',
                    duration: opp.commitment?.duration || '',
                    isPaid: opp.compensation?.isPaid || false,
                    compensationDetails: opp.compensation?.details || '',
                    capacity: opp.capacity || '',
                    applicationMethod:
                        opp.application?.method || 'external_link',
                    applicationUrl: opp.application?.url || '',
                    applicationEmail: opp.application?.email || '',
                    applicationDeadline: opp.application?.deadline
                        ? opp.application.deadline.substring(0, 10)
                        : '',
                    isRolling: opp.application?.isRolling || false,
                    requirements: (opp.requirements || []).join('\n'),
                    skillsDeveloped: (opp.skillsDeveloped || []).join('\n'),
                    contactName: opp.contact?.name || '',
                    contactEmail: opp.contact?.email || '',
                    contactPhone: opp.contact?.phone || '',
                    eventStartTime: opp.event?.startTime || '',
                    eventEndTime: opp.event?.endTime || '',
                    eventTimezone: opp.event?.timezone || '',
                    eventIsRecurring: opp.event?.isRecurring || false,
                    recurrencePattern: opp.event?.recurrencePattern || '',
                    recurrenceEndDate: opp.event?.recurrenceEndDate
                        ? opp.event.recurrenceEndDate.substring(0, 10)
                        : '',
                    registrationRequired:
                        opp.event?.registrationRequired || false,
                    registrationUrl: opp.event?.registrationUrl || '',
                    virtualLink: opp.event?.virtualLink || '',
                    fundingType: opp.funding?.fundingType || '',
                    fundingAmountMin: opp.funding?.amount?.min || '',
                    fundingAmountMax: opp.funding?.amount?.max || '',
                    fundingIsEstimate: opp.funding?.amount?.isEstimate || false,
                    fundingFunder: opp.funding?.funder || '',
                    fundingAwardCycle: opp.funding?.awardCycle || '',
                    fundingNumberOfAwards: opp.funding?.numberOfAwards || '',
                    fundingDuration: opp.funding?.fundingDuration || '',
                    fundingEligibleOrgTypes:
                        opp.funding?.eligibleOrgTypes || [],
                    fundingEligibleActivities: (
                        opp.funding?.eligibleActivities || []
                    ).join('\n'),
                    fundingMatchRequired: opp.funding?.matchRequired || false,
                    fundingMatchDetails: opp.funding?.matchDetails || '',
                    fundingSeekingPartners:
                        opp.funding?.seekingPartners || false,
                    fundingPartnerRequirements:
                        opp.funding?.partnerRequirements || '',
                    fundingPartnerContactName:
                        opp.funding?.partnerContact?.name || '',
                    fundingPartnerContactEmail:
                        opp.funding?.partnerContact?.email || '',
                });
            } catch (err) {
                console.error('Failed to load opportunity:', err);
                setError('Failed to load opportunity for editing.');
            } finally {
                setLoading(false);
            }
        };

        loadOpportunity();
    }, [mode, id, opportunityApi]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleMultiSelect = (name, value) => {
        setFormData((prev) => {
            const current = prev[name] || [];
            if (current.includes(value)) {
                return { ...prev, [name]: current.filter((v) => v !== value) };
            }
            return { ...prev, [name]: [...current, value] };
        });
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateStep = (stepNum) => {
        const errors = {};
        const currentStep = steps.find((s) => s.num === stepNum);
        if (!currentStep) return true;

        switch (currentStep.title) {
            case 'Basics':
                if (!formData.title?.trim()) errors.title = 'Title is required';
                if (!formData.type) errors.type = 'Type is required';
                if (!formData.description?.trim()) {
                    errors.description = 'Description is required';
                }
                break;

            case 'Application & Contact':
                if (
                    formData.applicationMethod === 'external_link' &&
                    !formData.applicationUrl?.trim()
                ) {
                    errors.applicationUrl = 'Application URL is required';
                }
                if (
                    formData.applicationMethod === 'email' &&
                    !formData.applicationEmail?.trim()
                ) {
                    errors.applicationEmail = 'Application email is required';
                }
                if (
                    formData.contactEmail &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
                ) {
                    errors.contactEmail = 'Please enter a valid email';
                }
                break;

            case 'Schedule & Commitment':
                if (
                    formData.startDate &&
                    formData.endDate &&
                    !formData.isOngoing &&
                    formData.endDate < formData.startDate
                ) {
                    errors.endDate = 'End date must be after start date';
                }
                break;

            case 'Event Details':
                if (!formData.eventStartTime) {
                    errors.eventStartTime = 'Start time is required';
                }
                break;

            case 'Funding Details':
                if (!formData.fundingType) {
                    errors.fundingType = 'Funding type is required';
                }
                if (
                    formData.fundingAmountMin &&
                    formData.fundingAmountMax &&
                    Number(formData.fundingAmountMax) <
                        Number(formData.fundingAmountMin)
                ) {
                    errors.fundingAmountMin =
                        'Min amount cannot exceed max amount';
                }
                break;

            default:
                break;
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildSubmitData = () => {
        const splitList = (str) =>
            str
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
        const splitComma = (str) =>
            str
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);

        const data = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            organization: orgId,
            type: formData.type,
            targetAudience: formData.targetAudience,
            focusAreas: splitComma(formData.focusAreas),
            regions: formData.regions,
            tags: splitComma(formData.tags),
            location: {
                isRemote:
                    formData.locationMode === 'remote' ||
                    formData.locationMode === 'hybrid',
                isInPerson:
                    formData.locationMode === 'in-person' ||
                    formData.locationMode === 'hybrid',
                isHybrid: formData.locationMode === 'hybrid',
                // A remote opportunity has no physical venue: these fields
                // (including venueName) are hidden from the form for that
                // mode, but the pre-fill effect can populate address/city/
                // state/zipCode from the org before the user ever picks a
                // mode (it fires on mount, while locationMode is still its
                // default 'in-person'). Omit them here rather than clearing
                // formData on toggle, so switching back to in-person
                // doesn't lose anything -- mirrors the server-side
                // isInPerson gate in applyOrgLocationDefaults.
                ...(formData.locationMode !== 'remote'
                    ? {
                          venueName: formData.venueName?.trim() || undefined,
                          address: formData.address?.trim() || undefined,
                          city: formData.city?.trim() || undefined,
                          state: formData.state?.trim() || undefined,
                          zipCode: formData.zipCode?.trim() || undefined,
                          // No form field edits this; round-tripped from the
                          // loaded opportunity (or left undefined on create)
                          // so update()'s whole-object Object.assign
                          // doesn't silently wipe it -- the same class of
                          // bug fixed for event.timezone above. Guarded
                          // against a malformed point: Joi rejects anything
                          // but exactly 2 coordinates, so a stored 1- or
                          // 3-length point (there's no path that produces
                          // one today) is normalized away here rather than
                          // surfaced -- silently dropped on the next save,
                          // not repaired.
                          coordinates:
                              formData.coordinates?.coordinates?.length === 2
                                  ? formData.coordinates
                                  : undefined,
                      }
                    : {}),
            },
            dates: {
                startDate: formData.startDate || undefined,
                endDate:
                    !formData.isOngoing && formData.endDate
                        ? formData.endDate
                        : undefined,
                isOngoing: formData.isOngoing,
            },
            commitment: {
                type: formData.commitmentType || undefined,
                hoursPerWeek: formData.hoursPerWeek
                    ? Number(formData.hoursPerWeek)
                    : undefined,
                duration: formData.duration?.trim() || undefined,
            },
            compensation: {
                isPaid: formData.isPaid,
                details: formData.compensationDetails?.trim() || undefined,
            },
            application: {
                method: formData.applicationMethod,
                url:
                    formData.applicationMethod === 'external_link'
                        ? formData.applicationUrl?.trim()
                        : undefined,
                email:
                    formData.applicationMethod === 'email'
                        ? formData.applicationEmail?.trim()
                        : undefined,
                deadline:
                    !formData.isRolling && formData.applicationDeadline
                        ? formData.applicationDeadline
                        : undefined,
                isRolling: formData.isRolling,
            },
            capacity: formData.capacity ? Number(formData.capacity) : undefined,
            requirements: splitList(formData.requirements),
            skillsDeveloped: splitList(formData.skillsDeveloped),
            contact: {
                name: formData.contactName?.trim() || undefined,
                email: formData.contactEmail?.trim() || undefined,
                phone: formData.contactPhone?.trim() || undefined,
            },
        };

        // Event fields (conditional)
        if (isEventType(formData.type)) {
            data.event = {
                isEvent: true,
                startTime: formData.eventStartTime || undefined,
                endTime: formData.eventEndTime || undefined,
                timezone: formData.eventTimezone,
                isRecurring: formData.eventIsRecurring,
                recurrencePattern: formData.eventIsRecurring
                    ? formData.recurrencePattern || undefined
                    : undefined,
                recurrenceEndDate: formData.eventIsRecurring
                    ? formData.recurrenceEndDate || undefined
                    : undefined,
                registrationRequired: formData.registrationRequired,
                registrationUrl: formData.registrationRequired
                    ? formData.registrationUrl?.trim() || undefined
                    : undefined,
                virtualLink: formData.virtualLink?.trim() || undefined,
            };
        } else {
            // `update()` replaces `opportunity.event` wholesale with
            // whatever is sent (no per-field merge), so a minimal
            // `{isEvent: false}` here would silently wipe a stored
            // timezone on every save of a non-event opportunity -- the
            // one event sub-field this ticket (#307) derives from the
            // owning org regardless of type. Round-trip it even though
            // the Event Details step never renders for this type.
            data.event = {
                isEvent: false,
                timezone: formData.eventTimezone || undefined,
            };
        }

        // Funding fields (conditional)
        if (isFundingType(formData.type)) {
            data.funding = {
                isFunding: true,
                fundingType: formData.fundingType || undefined,
                amount: {
                    min: formData.fundingAmountMin
                        ? Number(formData.fundingAmountMin)
                        : undefined,
                    max: formData.fundingAmountMax
                        ? Number(formData.fundingAmountMax)
                        : undefined,
                    isEstimate: formData.fundingIsEstimate,
                },
                funder: formData.fundingFunder?.trim() || undefined,
                awardCycle: formData.fundingAwardCycle || undefined,
                numberOfAwards: formData.fundingNumberOfAwards
                    ? Number(formData.fundingNumberOfAwards)
                    : undefined,
                fundingDuration: formData.fundingDuration?.trim() || undefined,
                eligibleOrgTypes: formData.fundingEligibleOrgTypes,
                eligibleActivities: splitList(
                    formData.fundingEligibleActivities
                ),
                matchRequired: formData.fundingMatchRequired,
                matchDetails: formData.fundingMatchRequired
                    ? formData.fundingMatchDetails?.trim() || undefined
                    : undefined,
                seekingPartners: formData.fundingSeekingPartners,
                partnerRequirements: formData.fundingSeekingPartners
                    ? formData.fundingPartnerRequirements?.trim() || undefined
                    : undefined,
                partnerContact: formData.fundingSeekingPartners
                    ? {
                          name:
                              formData.fundingPartnerContactName?.trim() ||
                              undefined,
                          email:
                              formData.fundingPartnerContactEmail?.trim() ||
                              undefined,
                      }
                    : undefined,
            };
        } else {
            data.funding = { isFunding: false };
        }

        return data;
    };

    const handleSubmit = async (publishAfter = false) => {
        setSaving(true);
        setError('');

        try {
            const submitData = buildSubmitData();
            let result;

            if (mode === 'edit') {
                result = await opportunityApi.updateOpportunity(id, submitData);
            } else {
                result = await opportunityApi.createOpportunity(submitData);
            }

            if (publishAfter && result?._id) {
                await opportunityApi.publishOpportunity(result._id);
            }

            setSuccess(
                mode === 'edit'
                    ? 'Opportunity updated successfully!'
                    : publishAfter
                      ? 'Opportunity created and published!'
                      : 'Opportunity saved as draft!'
            );

            setTimeout(() => {
                navigate(`/organizations/${orgId}`);
            }, 1500);
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Failed to save opportunity. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    const nextStep = () => {
        if (validateStep(step)) {
            // Find the next step number in the dynamic steps array
            const currentIndex = steps.findIndex((s) => s.num === step);
            if (currentIndex < steps.length - 1) {
                setStep(steps[currentIndex + 1].num);
            }
            setError('');
        }
    };

    const prevStep = () => {
        const currentIndex = steps.findIndex((s) => s.num === step);
        if (currentIndex > 0) {
            setStep(steps[currentIndex - 1].num);
        }
    };

    const goToStep = (stepNum) => {
        if (stepNum <= step) {
            setStep(stepNum);
        }
    };

    // Render current step content
    const renderStepContent = () => {
        const currentStep = steps.find((s) => s.num === step);
        if (!currentStep) return null;

        switch (currentStep.title) {
            case 'Basics':
                return (
                    <StepBasics
                        formData={formData}
                        handleChange={handleChange}
                        validationErrors={validationErrors}
                        opportunityTypes={opportunityTypes}
                    />
                );
            case 'Audience & Location':
                return (
                    <StepAudienceLocation
                        formData={formData}
                        handleChange={handleChange}
                        handleMultiSelect={handleMultiSelect}
                        validationErrors={validationErrors}
                        targetAudiences={targetAudiences}
                        regions={regions}
                    />
                );
            case 'Schedule & Commitment':
                return (
                    <StepScheduleCommitment
                        formData={formData}
                        handleChange={handleChange}
                        validationErrors={validationErrors}
                    />
                );
            case 'Application & Contact':
                return (
                    <StepApplicationContact
                        formData={formData}
                        handleChange={handleChange}
                        validationErrors={validationErrors}
                    />
                );
            case 'Event Details':
                return (
                    <StepEventDetails
                        formData={formData}
                        handleChange={handleChange}
                        validationErrors={validationErrors}
                        orgTimezone={orgLocation?.timezone}
                    />
                );
            case 'Funding Details':
                return (
                    <StepFundingDetails
                        formData={formData}
                        handleChange={handleChange}
                        handleMultiSelect={handleMultiSelect}
                        validationErrors={validationErrors}
                        organizationSectors={organizationSectors}
                    />
                );
            case 'Review':
                return (
                    <StepReview
                        formData={formData}
                        goToStep={goToStep}
                        steps={steps}
                    />
                );
            default:
                return null;
        }
    };

    // Loading states
    if (authLoading || authorized === null || (mode === 'edit' && loading)) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (authorized === false) {
        // If the user is signed in to Firebase but has no full RILE Connect
        // profile yet, surface the fix-it path rather than a flat denial.
        // The previous guard infinite-spun on this exact case (v1.0.1 #50).
        const isMissingProfile = firebaseUser && !currentUser;
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    {isMissingProfile ? (
                        <>
                            You need to create your RILE Connect profile before
                            you can manage opportunities.
                            <div className="mt-3">
                                <Link
                                    to="/create-profile"
                                    className="btn btn-primary"
                                >
                                    Create Profile
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            You do not have permission to manage opportunities
                            for this organization.
                            <div className="mt-3">
                                <Link
                                    to={`/organizations/${orgId}`}
                                    className="btn btn-outline-primary"
                                >
                                    Back to Organization
                                </Link>
                            </div>
                        </>
                    )}
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>
                    {mode === 'edit' ? 'Edit' : 'Create'} Opportunity |{' '}
                    {orgName} | RILE Connect
                </title>
            </Helmet>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <Link
                            to={`/organizations/${orgId}`}
                            className="text-primary d-inline-block mb-3"
                        >
                            ← Back to {orgName}
                        </Link>

                        <h1 className="text-primary mb-2">
                            {mode === 'edit'
                                ? 'Edit Opportunity'
                                : 'Create Opportunity'}
                        </h1>
                        <p className="text-muted mb-4">
                            {mode === 'edit'
                                ? 'Update the details of your opportunity.'
                                : `Post a new opportunity for ${orgName}.`}
                        </p>

                        {/* Step indicators */}
                        <div className="d-flex justify-content-between mb-4">
                            {steps.map((s) => (
                                <div
                                    key={s.num}
                                    className={`text-center flex-fill ${
                                        s.num <= step
                                            ? 'text-primary opportunity-form-step-indicator'
                                            : 'text-muted'
                                    }`}
                                    onClick={() => goToStep(s.num)}
                                    role="button"
                                    tabIndex={s.num <= step ? 0 : -1}
                                    onKeyDown={(e) =>
                                        e.key === 'Enter' && goToStep(s.num)
                                    }
                                >
                                    <div
                                        className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-2 step-circle ${
                                            s.num < step
                                                ? 'bg-success text-white'
                                                : s.num === step
                                                  ? 'bg-primary text-white'
                                                  : 'bg-light text-muted'
                                        }`}
                                    >
                                        {s.num < step ? '✓' : s.icon}
                                    </div>
                                    <div className="small d-none d-md-block">
                                        {s.title}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <ProgressBar
                            now={
                                (steps.findIndex((s) => s.num === step) /
                                    (totalSteps - 1)) *
                                100
                            }
                            className="mb-4"
                            variant="primary"
                        />

                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}

                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSubmit(false);
                                    }}
                                >
                                    {renderStepContent()}

                                    {/* Navigation */}
                                    <div className="d-flex justify-content-between mt-4">
                                        {steps.findIndex(
                                            (s) => s.num === step
                                        ) > 0 ? (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={prevStep}
                                                disabled={saving}
                                            >
                                                Previous
                                            </Button>
                                        ) : (
                                            <div />
                                        )}

                                        {!isReviewStep ? (
                                            <Button
                                                variant="primary"
                                                onClick={nextStep}
                                            >
                                                Next
                                            </Button>
                                        ) : (
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={() =>
                                                        handleSubmit(false)
                                                    }
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                className="me-2"
                                                            />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        'Save as Draft'
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    onClick={() =>
                                                        handleSubmit(true)
                                                    }
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                className="me-2"
                                                            />
                                                            Publishing...
                                                        </>
                                                    ) : (
                                                        'Save & Publish'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
